import type { StudioDesignSystemReleaseIdentity } from "@/features/project/lib/studio-project-plan";
import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from "@/services/cogo-platform-config";

type ActivationScope = "component" | "project";

export interface DesignSystemReleaseOption {
  schemaVersion: "cogo.design-system-release-option.v1";
  name: string;
  description: string;
  packageId: string;
  packageVersion: string;
  releaseId: string;
  packageSnapshotHash: string;
  identityHash: string;
  catalogContentHash: string;
  immutable: true;
  status: "released";
  activationScopes: ActivationScope[];
  targetPlatforms: string[];
  componentIds: string[];
}

export interface FetchDesignSystemReleasesResult {
  releases: DesignSystemReleaseOption[];
  error?: string;
}

const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function parseRelease(value: unknown): DesignSystemReleaseOption | null {
  if (!isRecord(value)) return null;
  const activationScopes = Array.isArray(value.activationScopes)
    ? value.activationScopes.filter(
        (scope): scope is ActivationScope =>
          scope === "component" || scope === "project",
      )
    : [];
  const targetPlatforms = Array.isArray(value.targetPlatforms)
    ? value.targetPlatforms.filter(
        (target): target is string =>
          typeof target === "string" && target.trim().length > 0,
      )
    : [];
  const componentIds = Array.isArray(value.componentIds)
    ? value.componentIds.filter(
        (component): component is string =>
          typeof component === "string" && component.trim().length > 0,
      )
    : [];
  if (
    value.schemaVersion !== "cogo.design-system-release-option.v1" ||
    typeof value.name !== "string" ||
    !value.name.trim() ||
    typeof value.description !== "string" ||
    typeof value.packageId !== "string" ||
    !value.packageId.trim() ||
    typeof value.packageVersion !== "string" ||
    !value.packageVersion.trim() ||
    typeof value.releaseId !== "string" ||
    !value.releaseId.trim() ||
    typeof value.packageSnapshotHash !== "string" ||
    !CONTENT_HASH_PATTERN.test(value.packageSnapshotHash) ||
    typeof value.identityHash !== "string" ||
    !CONTENT_HASH_PATTERN.test(value.identityHash) ||
    typeof value.catalogContentHash !== "string" ||
    !CONTENT_HASH_PATTERN.test(value.catalogContentHash) ||
    value.immutable !== true ||
    value.status !== "released" ||
    activationScopes.length === 0 ||
    targetPlatforms.length === 0
  ) {
    return null;
  }
  return {
    schemaVersion: "cogo.design-system-release-option.v1",
    name: value.name.trim(),
    description: value.description,
    packageId: value.packageId.trim(),
    packageVersion: value.packageVersion.trim(),
    releaseId: value.releaseId.trim(),
    packageSnapshotHash: value.packageSnapshotHash,
    identityHash: value.identityHash,
    catalogContentHash: value.catalogContentHash,
    immutable: true,
    status: "released",
    activationScopes: [...new Set(activationScopes)],
    targetPlatforms: [...new Set(targetPlatforms)],
    componentIds: [...new Set(componentIds)],
  };
}

export function projectReleaseBinding(
  release: DesignSystemReleaseOption,
): StudioDesignSystemReleaseIdentity | null {
  if (!release.activationScopes.includes("project")) return null;
  return {
    schemaVersion: "cogo.studio-design-system-release-binding.v1",
    packageId: release.packageId,
    packageVersion: release.packageVersion,
    releaseId: release.releaseId,
    packageSnapshotHash: release.packageSnapshotHash,
    identityHash: release.identityHash,
    catalogContentHash: release.catalogContentHash,
    immutable: true,
    activationScope: "project",
    targetPlatforms: release.targetPlatforms,
  };
}

export async function fetchDesignSystemReleasesViaWorker(): Promise<FetchDesignSystemReleasesResult> {
  const jwt = await getStudioAccessToken();
  if (!jwt) return { releases: [], error: "Sign in required" };
  try {
    const response = await fetch(
      cogoPlatformApiUrl("/api/design-system-releases"),
      {
        headers: withCogoDevProxyHeaderRecord({
          Authorization: `Bearer ${jwt}`,
        }),
      },
    );
    if (!response.ok) {
      return {
        releases: [],
        error: `Release index unavailable (HTTP ${response.status})`,
      };
    }
    const body = await response.json();
    if (
      !isRecord(body) ||
      body.schemaVersion !== "cogo.design-system-release-index.v1" ||
      !Array.isArray(body.releases)
    ) {
      return { releases: [], error: "Release index contract is invalid" };
    }
    const parsed = body.releases
      .map(parseRelease)
      .filter((release): release is DesignSystemReleaseOption =>
        Boolean(release),
      );
    const releases = [
      ...new Map(
        parsed.map((release) => [
          `${release.packageId}@${release.releaseId}`,
          release,
        ]),
      ).values(),
    ];
    return { releases };
  } catch (error) {
    return {
      releases: [],
      error:
        error instanceof Error ? error.message : "Release index unavailable",
    };
  }
}
